import axios, { AxiosResponse } from 'axios';
import { inject } from '@angular/core';
import { UserService, User, AuthService } from 'capiche-auth';
import { HttpResponse } from './response.interface';
import { CapicheLoggerService } from 'capiche-logger';

export class ApiService {
  logger = new CapicheLoggerService('api.service:', false);

  private authToken: string | undefined = undefined;
  currentUser: User | null = null;

  // CSW-1651: a 401 from these APIs means the session is actually invalid
  // (revoked or expired), not a transient failure — see
  // FirebaseAppService.checkAuth (checkRevoked:true). Every caller of
  // get/post across the extension, web app and teacher portal goes through
  // this one base class, so this is the single place that can react
  // consistently instead of leaving each caller (suggestions, usage
  // counters, etc.) to fail silently on its own, which is what was
  // happening before: 401s in the console, editor just quietly stops
  // working, nothing tells the user they've been signed out.
  private readonly authService = inject(AuthService);

  constructor(private readonly userService: UserService) {
    this.userService.currentUser$.subscribe((user) => {
      this.currentUser = user;
    });
  }

  /**
   * CSW-1651: forces a real sign-out once the backend has told us the
   * session is invalid, rather than leaving a half-authenticated client
   * state around (Firebase's own token-refresh path only notices this
   * asynchronously, on its own schedule — see _logoutIfInvalidated in the
   * SDK). Each app already has a live (not just on-navigation) redirect
   * driven off currentUser$ going null — e.g. app-capiche-web-app's
   * app.component.ts — so signing out here is enough to get the user
   * moved to a login screen without this shared library needing to know
   * about app-specific routes.
   */
  private handleUnauthorized(error: any): boolean {
    if (error?.response?.status !== 401) return false;
    this.logger.warn('401 from API — session invalid, signing out');
    this.authService.logout().catch((err) => this.logger.error('sign-out after 401 failed:', err));
    return true;
  }

  protected async setAuthToken(): Promise<void> {
    /**
     * Sets a JWT access token for the current user
     */
    try {
      this.authToken = await this.currentUser?.getIdToken();
    } catch (error) {
      this.logger.error(error);
    }
  }

  protected async get<T>(endpoint: string): Promise<HttpResponse | null> {
    if (!this.currentUser) {
      this.logger.info(`Skipped GET ${endpoint}: no current user`);
      return null;
    }

    try {
      await this.setAuthToken();
      if (!this.authToken) {
        this.logger.warn(`No token available, dropping GET ${endpoint}`);
        return null;
      }

      const response: AxiosResponse = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${this.authToken}` },
      });

      return new HttpResponse(response);
    } catch (error: any) {
      this.handleUnauthorized(error);
      this.handleError(error);
      return null;
    }
  }

  protected async post<T>(endpoint: string, data: object = {}): Promise<HttpResponse | null> {
    if (!this.currentUser) {
      this.logger.info(`Skipped POST ${endpoint}: no current user`);
      return null;
    }

    try {
      await this.setAuthToken();
      if (!this.authToken) {
        this.logger.warn(`No token available, dropping POST ${endpoint}`);
        return null;
      }

      const response: AxiosResponse = await axios.post(endpoint, data, {
        headers: { Authorization: `Bearer ${this.authToken}` },
      });

      return new HttpResponse(response);
    } catch (error: any) {
      this.handleUnauthorized(error);
      this.handleError(error);
      return {
        error: { originalError: error, status: error.response?.status },
      } as HttpResponse;
    }
  }

  protected handleError(error: Error): void {
    // Something happened in setting up the request that triggered an error
    this.logger.error('error setting up the request:', error);
  }
}
